import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { getOverview } from './overviewApi';

export interface OverviewData {
    upload_every_day: Record<string, number>;
    error_every_day: Record<string, number>;
    records: { Items: any[] };
    upload_day: number;
    error_day: number;
    upload_week: number;
    error_week: number;
    normal: Record<string, number>;
    abnormal: Record<string, number>;
    fail_average_time: number;
    fail_rate: number;
}

export const overviewKeys = {
    all: ['overview'] as const,
    range: (start: string, end: string) => [...overviewKeys.all, start, end] as const,
};

/**
 * Overview stats for the days in `timeKeys` (YYYY-MM-DD, first to last).
 *
 * `keepPreviousData` holds the last range on screen while a new one loads, so
 * `isPending` is only true on route entry. That's the "skeletons on first load,
 * no flash on range switch" behaviour the page relies on.
 */
export const useOverviewQuery = (timeKeys: string[]) => {
    const start = `${timeKeys[0]} 00:00:00`;
    const end = `${timeKeys[timeKeys.length - 1]} 23:59:59`;

    return useQuery({
        queryKey: overviewKeys.range(start, end),
        queryFn: async () => {
            const data = await getOverview(start, end);
            // The API reports failures as HTTP 200 with status "fail"; throw so
            // the query treats it as an error instead of caching it as data.
            if (data?.status === 'fail') {
                throw new Error(data.message ?? 'getOverview failed');
            }
            return data as OverviewData;
        },
        placeholderData: keepPreviousData,
    });
};
